import { memo, useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useGuest } from "@/contexts/GuestContext";
import { useDismissedSuggestions } from "@/hooks/useDismissedSuggestions";
import { useBlockedUsers } from "@/hooks/useBlockedUsers";
import { SuggestionCard } from "./SuggestionCard";
import { SuggestionsSheet } from "./SuggestionsSheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import type { FriendSuggestion } from "@/utils/friendSuggestions";

export const FriendSuggestionsCarousel = memo(function FriendSuggestionsCarousel() {
  const { user } = useAuth();
  const { isGuest } = useGuest();
  const queryClient = useQueryClient();
  const { dismissedIds, dismiss } = useDismissedSuggestions();
  const { blockedUsers } = useBlockedUsers();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSheet, setShowSheet] = useState(false);
  
  const blockedUserIds = blockedUsers.map(b => b.blocked_id);

  // Don't show anything for guests
  if (isGuest) return null;

  const { data: allSuggestions = [], isLoading } = useQuery({
    queryKey: ["friend-suggestions", user?.id, blockedUserIds],
    queryFn: async () => {
      if (!user) return [];

      // OPTIMIZED: Single query to get connections (both directions)
      const { data: connections } = await supabase
        .from("user_connections")
        .select("user_id, connected_user_id")
        .or(`user_id.eq.${user.id},connected_user_id.eq.${user.id}`);

      const connectedUserIds = connections?.map(c => 
        c.user_id === user.id ? c.connected_user_id : c.user_id
      ) || [];
      
      // Exclude connected users, self, and blocked users
      const excludeIds = [...connectedUserIds, user.id, ...blockedUserIds];

      // OPTIMIZED: Batch fetch user data and events/herds in parallel
      const [profileResult, eventsResult, herdsResult, potentialUsersResult] = await Promise.all([
        supabase
          .from("profiles")
          .select("location")
          .eq("id", user.id)
          .single(),
        supabase
          .from("event_attendances")
          .select("event_id")
          .eq("user_id", user.id),
        supabase
          .from("herd_members")
          .select("herd_id")
          .eq("user_id", user.id),
        supabase
          .rpc("get_profiles_for_suggestions", { result_limit: 50 })
      ]);

      const myProfile = profileResult.data;
      const myEventIds = eventsResult.data?.map(e => e.event_id) || [];
      const myHerdIds = herdsResult.data?.map(h => h.herd_id) || [];
      // Filter out excluded IDs client-side since RPC doesn't support .not() filtering
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const potentialUsers = (potentialUsersResult.data || []).filter((u: any) => !excludeIds.includes(u.id));

      if (potentialUsers.length === 0) return [];

      // OPTIMIZED: Fetch all shared events/herds counts in batch queries
      const potentialUserIds = potentialUsers.map(u => u.id);
      
      const [sharedEventsResult, sharedHerdsResult, friendsOfFriendsResult] = await Promise.all([
        // Get shared events count for all potential users in one query
        myEventIds.length > 0 
          ? supabase
              .from("event_attendances")
              .select("user_id, event_id")
              .in("user_id", potentialUserIds)
              .in("event_id", myEventIds)
          : Promise.resolve({ data: [] }),
        // Get shared herds count for all potential users in one query
        myHerdIds.length > 0
          ? supabase
              .from("herd_members")
              .select("user_id, herd_id")
              .in("user_id", potentialUserIds)
              .in("herd_id", myHerdIds)
          : Promise.resolve({ data: [] }),
        // Get friends of friends (mutual connections)
        connectedUserIds.length > 0
          ? supabase
              .from("user_connections")
              .select("user_id, connected_user_id")
              .or(`user_id.in.(${connectedUserIds.join(",")}),connected_user_id.in.(${connectedUserIds.join(",")})`)
              .eq("status", "accepted")
          : Promise.resolve({ data: [] })
      ]);

      // Build counts maps for O(1) lookups
      const sharedEventsCounts = new Map<string, number>();
      (sharedEventsResult.data || []).forEach(item => {
        sharedEventsCounts.set(item.user_id, (sharedEventsCounts.get(item.user_id) || 0) + 1);
      });

      const sharedHerdsCounts = new Map<string, number>();
      (sharedHerdsResult.data || []).forEach(item => {
        sharedHerdsCounts.set(item.user_id, (sharedHerdsCounts.get(item.user_id) || 0) + 1);
      });

      const friendsOfFriendsIds = new Set(
        (friendsOfFriendsResult.data || [])
          .map(f => connectedUserIds.includes(f.user_id) ? f.connected_user_id : f.user_id)
          .filter(id => !excludeIds.includes(id))
      );

      // Calculate scores for each user (no more N+1 queries!)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const scoredSuggestions: FriendSuggestion[] = potentialUsers.map((potentialUser: any) => {
        const sharedEvents = sharedEventsCounts.get(potentialUser.id) || 0;
        const sharedHerds = sharedHerdsCounts.get(potentialUser.id) || 0;
        const mutualFriends = friendsOfFriendsIds.has(potentialUser.id) ? 1 : 0;
        const sameLocation = myProfile?.location === potentialUser.location && !!myProfile?.location;

        // Calculate score inline (avoid additional function call overhead)
        let score = 0;
        score += sharedEvents * 3;
        score += sharedHerds * 4;
        score += mutualFriends * 5;
        score += sameLocation ? 2 : 0;

        // Generate reasons inline
        const reasons: string[] = [];
        if (sharedEvents > 0) reasons.push(`${sharedEvents} shared event${sharedEvents > 1 ? 's' : ''}`);
        if (sharedHerds > 0) reasons.push(`${sharedHerds} shared herd${sharedHerds > 1 ? 's' : ''}`);
        if (mutualFriends > 0) reasons.push('Mutual friends');
        if (sameLocation) reasons.push('Same location');

        return {
          userId: potentialUser.id,
          username: potentialUser.username,
          full_name: potentialUser.full_name,
          avatar_url: potentialUser.avatar_url,
          score,
          reasons: reasons.length > 0 ? reasons : ['Recently joined'],
          sharedEvents,
          sharedHerds,
          mutualFriends,
          subscription_tier: potentialUser.subscription_tier,
          subscription_status: potentialUser.subscription_status,
          is_founding_member: potentialUser.is_founding_member,
        };
      });

      // Sort by score and return top suggestions
      return scoredSuggestions
        .sort((a, b) => b.score - a.score)
        .slice(0, 20);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes - suggestions don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes cache
  });

  // Filter out dismissed suggestions - memoized
  const suggestions = useMemo(() => 
    allSuggestions.filter(s => !dismissedIds.includes(s.userId)),
    [allSuggestions, dismissedIds]
  );

  // Get current 2 suggestions - memoized
  const displayedSuggestions = useMemo(() => 
    suggestions.slice(currentIndex, currentIndex + 2),
    [suggestions, currentIndex]
  );

  const sendRequest = useMutation({
    mutationKey: ['friend-request-send'],
    mutationFn: async (toUserId: string) => {
      // Check if I sent a request to this user
      const { data: sentByMe } = await supabase
        .from("user_connections")
        .select("*")
        .eq("user_id", user?.id)
        .eq("connected_user_id", toUserId);

      // Check if this user sent a request to me
      const { data: sentToMe } = await supabase
        .from("user_connections")
        .select("*")
        .eq("user_id", toUserId)
        .eq("connected_user_id", user?.id);

      const existingConnections = [...(sentByMe || []), ...(sentToMe || [])];

      if (existingConnections.length > 0) {
        const existing = existingConnections[0];
        dismiss(toUserId);
        queryClient.invalidateQueries({ queryKey: ["friend-suggestions", user?.id] });
        queryClient.invalidateQueries({ queryKey: ["sent-requests", user?.id] });
        queryClient.invalidateQueries({ queryKey: ["connection-status"] });

        if (existing.status === 'accepted') {
          toast.info("You're already friends!");
          throw new Error("already_friends");
        } else if (existing.user_id === user?.id) {
          toast.info("Friend request already sent");
          throw new Error("already_sent");
        } else {
          toast.info("This user has sent you a friend request. Check your Requests tab!");
          throw new Error("incoming_request");
        }
      }

      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase.from("user_connections").insert({
        user_id: user.id,
        connected_user_id: toUserId,
        status: "pending",
      });

      if (error) throw error;
      return toUserId;
    },
    onSuccess: (_, toUserId) => {
      queryClient.invalidateQueries({ queryKey: ["friend-suggestions", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["connection-status"] });
      queryClient.invalidateQueries({ queryKey: ["sent-requests", user?.id] });
      dismiss(toUserId);
      toast.success("Friend request sent!");
    },
    onError: (error: Error) => {
      if (!['already_friends', 'already_sent', 'incoming_request'].includes(error.message)) {
        toast.error("Failed to send friend request");
      }
    },
  });

  const handleDismiss = useCallback((userId: string) => {
    dismiss(userId);
    setCurrentIndex(prev => {
      if (prev >= suggestions.length - 2) {
        return Math.max(0, prev - 1);
      }
      return prev;
    });
  }, [dismiss, suggestions.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => {
      if (prev + 2 < suggestions.length) {
        return prev + 1;
      }
      return prev;
    });
  }, [suggestions.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : prev);
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">People You May Know</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">People You May Know</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNext}
            disabled={currentIndex + 2 >= suggestions.length}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {displayedSuggestions.map((suggestion) => (
          <SuggestionCard
            key={suggestion.userId}
            suggestion={suggestion}
            onAddFriend={() => sendRequest.mutate(suggestion.userId)}
            onDismiss={() => handleDismiss(suggestion.userId)}
            isAdding={sendRequest.isPending}
            variant="carousel"
          />
        ))}
      </div>

      {suggestions.length > 2 && (
        <Button
          variant="link"
          onClick={() => setShowSheet(true)}
          className="w-full text-sm"
        >
          See All Suggestions ({Math.min(suggestions.length, 10)})
        </Button>
      )}

      <SuggestionsSheet
        open={showSheet}
        onOpenChange={setShowSheet}
        suggestions={suggestions.slice(0, 10)}
        isLoading={isLoading}
        onAddFriend={(userId) => sendRequest.mutate(userId)}
        isAdding={sendRequest.isPending}
      />
    </div>
  );
});
